import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const { login, resetPassword } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const getErrorMessage = (errorCode: string): string => {
    switch (errorCode) {
      case 'auth/user-not-found':
        return "Aucun compte trouvé avec cet email.";
      case 'auth/wrong-password':
        return "Mot de passe incorrect.";
      case 'auth/invalid-email':
        return "Format d'email invalide.";
      case 'auth/user-disabled':
        return "Ce compte a été désactivé.";
      case 'auth/too-many-requests':
        return "Trop de tentatives. Veuillez réessayer plus tard.";
      case 'auth/network-request-failed':
        return "Erreur de connexion. Vérifiez votre connexion internet.";
      default:
        return "Erreur de connexion. Vérifiez vos identifiants.";
    }
  };

  const getResetPasswordErrorMessage = (errorCode: string): string => {
    switch (errorCode) {
      case 'auth/user-not-found':
        return "Aucun compte trouvé avec cet email.";
      case 'auth/invalid-email':
        return "Format d'email invalide.";
      case 'auth/too-many-requests':
        return "Trop de tentatives. Veuillez réessayer plus tard.";
      case 'auth/network-request-failed':
        return "Erreur de connexion. Vérifiez votre connexion internet.";
      default:
        return "Une erreur est survenue. Veuillez réessayer plus tard.";
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!resetEmail.trim()) {
      toast({
        variant: "destructive",
        title: "Email requis",
        description: "Veuillez entrer votre adresse email.",
      });
      return;
    }

    setIsResetting(true);
    
    try {
      const result = await resetPassword(resetEmail);
      
      if (result.success) {
        toast({
          title: "Email envoyé",
          description: "Un email de réinitialisation a été envoyé à votre adresse. Vérifiez votre boîte de réception.",
        });
        setIsResetDialogOpen(false);
        setResetEmail('');
      } else {
        const errorMessage = getResetPasswordErrorMessage(result.error || '');
        toast({
          variant: "destructive",
          title: "Erreur",
          description: errorMessage,
        });
      }
    } catch (error: any) {
      const errorMessage = getResetPasswordErrorMessage(error?.code || '');
      toast({
        variant: "destructive",
        title: "Erreur",
        description: errorMessage,
      });
    } finally {
      setIsResetting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const result = await login(email, password);
      
      if (result.success) {
        toast({
          title: "Connexion réussie",
          description: "Bienvenue dans Weborama Enrichment Platform !",
        });
        navigate('/client-selection');
      } else {
        // Si c'est une erreur Firebase (mauvais identifiants), afficher le message
        // Les erreurs claims/api ont déjà affiché leur toast dans AuthContext
        if (result.errorType === 'firebase') {
        toast({
          variant: "destructive",
          title: "Erreur de connexion",
          description: "Identifiants incorrects.",
        });
        }
        // Pour claims et api, le toast a déjà été affiché dans AuthContext
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error?.code || '');
      toast({
        variant: "destructive",
        title: "Erreur de connexion",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
      <div className="relative w-full max-w-md">
        {/* Logo positionné de manière moderne (moitié sur gris, moitié sur noir) */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
          <img 
            src="/logo_W.png" 
            alt="Weborama Logo" 
            className="w-[64px] h-[64px] object-contain"
          />
        </div>
        <Card className="w-full shadow-card">
          <CardHeader className="text-center pt-8">
            <CardTitle className="text-2xl font-bold text-white">
              Weborama Enrichment Platform
          </CardTitle>
          <CardDescription>
            Connectez-vous à votre compte
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Entrez votre email"
                required
                autoComplete="email"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Entrez votre mot de passe"
                required
                autoComplete="current-password"
              />
            </div>
            
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setIsResetDialogOpen(true)}
                className="text-sm text-muted-foreground hover:text-primary underline-offset-4 hover:underline"
              >
                Mot de passe oublié ?
              </button>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-primary hover:bg-primary-hover"
              disabled={isLoading}
            >
              {isLoading ? 'Connexion...' : 'Se connecter'}
            </Button>
          </form>
        </CardContent>
      </Card>
      </div>

      <Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
            <DialogDescription>
              Entrez votre adresse email pour recevoir un lien de réinitialisation.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetPassword}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email</Label>
                <Input
                  id="reset-email"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="Entrez votre email"
                  required
                  autoComplete="email"
                  disabled={isResetting}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsResetDialogOpen(false);
                  setResetEmail('');
                }}
                disabled={isResetting}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={isResetting}
              >
                {isResetting ? 'Envoi...' : 'Envoyer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;